import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import { IoClose } from "react-icons/io5";
import { MdCheckCircle, MdTagFaces } from "react-icons/md";
import { TrainingBookCard } from "@/Components/TrainingBookCard";
import { useAuthStore } from "@/contexts/AuthContext";
import {
  START_ONBOARDING_TOUR_EVENT,
  clearOnboardingTourPending,
  isOnboardingTourAllowedForRole,
  isOnboardingTourPending,
} from "./tourStorage";

/*
 * First-run tour for non-manager users who have never been active, set up at
 * login (magic link or email/password) via syncOnboardingTourPending in
 * ./tourStorage. It only reads the DOM: nav/header items opt in with a
 * `data-tour="<id>"` attribute, and a step whose target is not rendered (e.g.
 * Feeds hidden for the org) is dropped. Nothing here touches real data. On
 * mobile the first step opens the Trainings page and spotlights the first real
 * training card, falling back to a dummy card when there are no trainings.
 */

// Pages that render the shared layout (header + nav) the tour points at.
const TOUR_PAGES = [
  "/home",
  "/feeds",
  "/dashboard",
  "/assignments",
  "/history",
];

const DEMO_TRAINING = {
  _id: "onboarding-tour-demo",
  title: "Product Sales Training",
  description:
    "Learn the product, practise the pitch and handle common objections.",
  completionPercentage: 0,
  published: true,
};

type TourStep = {
  id: string;
  /** data-tour ids to spotlight; null means the step renders its own demo card. */
  targets: string[] | null;
  titleKey: string;
  title: string;
  bodyKey: string;
  body: string;
  /** "right" puts the tooltip beside the target (desktop sidebar). */
  placement?: "auto" | "right";
  /** Page the step lives on; the tour navigates there when the step opens. */
  route?: string;
  /** Spotlight only the first match (e.g. the first of many training cards). */
  firstOnly?: boolean;
  /** Show the dummy training card if the target never renders (no trainings). */
  demoFallback?: boolean;
  /**
   * data-tour id the page renders once it knows there is nothing to spotlight
   * (empty trainings list). Switches to the dummy card immediately.
   */
  emptyTarget?: string;
};

// Mobile (< md): bottom nav + mobile header.
const MOBILE_STEPS: TourStep[] = [
  {
    id: "trainings",
    targets: ["training-card"],
    route: "/home",
    firstOnly: true,
    demoFallback: true,
    emptyTarget: "trainings-empty",
    titleKey: "tour_trainings_title",
    title: "Your trainings",
    bodyKey: "tour_trainings_body",
    body: "Everything assigned to you lives here. Open a training to start learning; the ring tracks your progress.",
  },
  {
    id: "feeds",
    targets: ["nav-feeds"],
    titleKey: "tour_feeds_title",
    title: "Feeds",
    bodyKey: "tour_feeds_body",
    body: "Short videos and updates shared by your organization.",
  },
  {
    id: "dashboard",
    targets: ["nav-dashboard"],
    titleKey: "tour_dashboard_title",
    title: "Dashboard",
    bodyKey: "tour_dashboard_body",
    body: "Your scores, completion and activity in one view.",
  },
  {
    id: "assignments",
    targets: ["nav-assignments"],
    titleKey: "tour_assignments_title",
    title: "Assignments",
    bodyKey: "tour_assignments_body",
    body: "Tasks with due dates, so nothing slips past you.",
  },
  {
    id: "history",
    targets: ["nav-history"],
    titleKey: "tour_history_title",
    title: "History",
    bodyKey: "tour_history_body",
    body: "Review past sessions and results whenever you like.",
  },
  {
    id: "alerts",
    targets: ["header-notifications", "header-support"],
    titleKey: "tour_alerts_title",
    title: "Alerts and help",
    bodyKey: "tour_alerts_body",
    body: "New assignments and updates appear on the bell. The headset opens support.",
  },
];

// Desktop (md+): sidebar, dashboard sections, header actions, AI assistant.
// Dashboard steps are dropped automatically on other pages.
const DESKTOP_STEPS: TourStep[] = [
  {
    id: "d-nav",
    targets: ["sidebar-nav"],
    placement: "right",
    titleKey: "tour_d_nav_title",
    title: "Find your way",
    bodyKey: "tour_d_nav_body",
    body: "Trainings, assignments, feeds and your history are one click away from this menu.",
  },
  {
    id: "d-rankings",
    targets: ["dash-rankings"],
    titleKey: "tour_d_rankings_title",
    title: "See where you stand",
    bodyKey: "tour_d_rankings_body",
    body: "Your rank in your team, zone, headquarters and organization. Ranks appear once you complete trainings.",
  },
  {
    id: "d-activity",
    targets: ["dash-activity"],
    titleKey: "tour_d_activity_title",
    title: "Track your progress",
    bodyKey: "tour_d_activity_body",
    body: "Trainings assigned, completed and in progress, plus the reward points you earn.",
  },
  {
    id: "d-pending",
    targets: ["dash-pending"],
    titleKey: "tour_d_pending_title",
    title: "Start here",
    bodyKey: "tour_d_pending_body",
    body: "Trainings and assignments waiting for you. Your first training is ready to start.",
  },
  {
    id: "d-header",
    targets: ["header-actions"],
    titleKey: "tour_d_header_title",
    title: "Help, rankings and alerts",
    bodyKey: "tour_d_header_body",
    body: "The headset opens support, the chart shows the leaderboard, the star shows your rewards and the bell brings new assignments.",
  },
  {
    id: "d-ai",
    targets: ["ai-assistant"],
    titleKey: "tour_d_ai_title",
    title: "Ask the AI coach",
    bodyKey: "tour_d_ai_body",
    body: "Questions about a training or a product? The AI coach answers any time.",
  },
];

const DESKTOP_QUERY = "(min-width: 768px)";
// The desktop tour walks through the dashboard, so it opens there first
// (mobile starts from its own "Your trainings" step on /home instead).
const DESKTOP_TOUR_PAGE = "/dashboard";
// Dashboard sections render after their data loads; wait for them (up to a
// limit) before deciding which desktop steps are available.
const DESKTOP_WAIT_TARGETS = ["dash-rankings", "dash-activity", "dash-pending"];
const DESKTOP_PREPARE_TIMEOUT_MS = 4000;

type Rect = { top: number; left: number; width: number; height: number };

const EDGE = 16;
const GAP = 14;
const SPOT_PAD = 4;
const SPOT_PAD_DESKTOP = 8;
const TOOLTIP_MAX_WIDTH = 300;
const TOOLTIP_WIDTH_DESKTOP = 260;
// Safety net for a demoFallback step: if neither its real target nor its
// emptyTarget shows up in this time (e.g. the list request is stuck), show the
// dummy card anyway.
const DEMO_FALLBACK_DELAY_MS = 2500;

const isOnScreen = (el: Element) => {
  const r = el.getBoundingClientRect();
  return (
    r.width > 0 &&
    r.height > 0 &&
    r.right > 0 &&
    r.bottom > 0 &&
    r.left < window.innerWidth &&
    r.top < window.innerHeight
  );
};

const targetExists = (id: string) =>
  document.querySelector(`[data-tour="${id}"]`) !== null;

/**
 * Union of every on-screen element tagged with the given ids (one id can tag
 * several elements, e.g. a section heading and its card grid), or null if none
 * are visible.
 */
const measureTargets = (ids: string[], firstOnly = false): Rect | null => {
  const elements = ids.flatMap((id) =>
    Array.from(document.querySelectorAll(`[data-tour="${id}"]`)).filter(
      isOnScreen
    )
  );
  const rects = (firstOnly ? elements.slice(0, 1) : elements).map((el) =>
    el.getBoundingClientRect()
  );
  if (!rects.length) return null;
  const top = Math.min(...rects.map((r) => r.top));
  const left = Math.min(...rects.map((r) => r.left));
  const right = Math.max(...rects.map((r) => r.right));
  const bottom = Math.max(...rects.map((r) => r.bottom));
  return { top, left, width: right - left, height: bottom - top };
};

const measureElement = (el: HTMLElement | null): Rect | null => {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
};

const OnboardingTour = () => {
  const { t } = useTranslation("common");
  const pathname = usePathname();
  const router = useRouter();
  const { user, org, role } = useAuthStore();
  // Managers never see the tour, even with a stale pending flag or a replay.
  const tourAllowed = isOnboardingTourAllowedForRole(role || user?.role);

  const [phase, setPhase] = useState<
    "idle" | "welcome" | "preparing" | "steps"
  >("idle");
  const [steps, setSteps] = useState<TourStep[]>(MOBILE_STEPS);
  const [isDesktop, setIsDesktop] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const [viewport, setViewport] = useState({ w: 0, h: 0 });
  const [doneToast, setDoneToast] = useState<string | null>(null);
  // True while a demoFallback step is drawing the dummy card.
  const [showingDemo, setShowingDemo] = useState(false);

  const demoCardRef = useRef<HTMLDivElement | null>(null);
  const primaryButtonRef = useRef<HTMLButtonElement | null>(null);

  const step = phase === "steps" ? steps[stepIndex] : null;
  const isLastStep = stepIndex === steps.length - 1;

  const start = useCallback(() => {
    setIsDesktop(window.matchMedia(DESKTOP_QUERY).matches);
    setStepIndex(0);
    setRect(null);
    setDoneToast(null);
    setPhase("welcome");
  }, []);

  const beginSteps = () => {
    // Desktop: move to the dashboard first; the "preparing" effect below
    // commits the steps once its sections have rendered.
    if (isDesktop && pathname !== DESKTOP_TOUR_PAGE) {
      setPhase("preparing");
      void router.push(DESKTOP_TOUR_PAGE);
      return;
    }
    commitSteps();
  };

  const finish = useCallback(() => {
    clearOnboardingTourPending();
    setPhase("idle");
    setRect(null);
    const desktop = window.matchMedia(DESKTOP_QUERY).matches;
    setDoneToast(
      desktop
        ? t(
            "tour_done_desktop",
            "You're all set. Click your profile picture to replay the tour."
          )
        : t(
            "tour_done_mobile",
            "You're all set. Tap your avatar to replay the tour."
          )
    );
  }, [t]);

  const commitSteps = useCallback(() => {
    // Filter when the tour actually starts (not at the welcome) so sections
    // that render after data loads, like the dashboard cards, are included.
    // Steps whose target isn't rendered for this user/org/page are dropped.
    const source = isDesktop ? DESKTOP_STEPS : MOBILE_STEPS;
    // Steps with a route live on another page, so their target can't be
    // checked from here; they are kept and resolved after navigating.
    const available = source.filter(
      (s) => !s.targets || s.route || s.targets.some((id) => targetExists(id))
    );
    if (!available.length) {
      finish();
      return;
    }
    setSteps(available);
    setStepIndex(0);
    setPhase("steps");
  }, [isDesktop, finish]);

  // Desktop: after navigating to the dashboard, wait for its sections, then
  // start. On timeout (slow data, or the redirect went elsewhere) start with
  // whatever is rendered; missing steps are dropped as usual.
  useEffect(() => {
    if (phase !== "preparing") return;
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      const ready =
        window.location.pathname === DESKTOP_TOUR_PAGE &&
        DESKTOP_WAIT_TARGETS.every((id) => targetExists(id));
      if (ready || Date.now() - startedAt >= DESKTOP_PREPARE_TIMEOUT_MS) {
        window.clearInterval(timer);
        commitSteps();
      }
    }, 200);
    return () => window.clearInterval(timer);
  }, [phase, commitSteps]);

  // Auto-start for eligible learners once a layout page has rendered its nav.
  useEffect(() => {
    if (phase !== "idle" || !pathname || !TOUR_PAGES.includes(pathname)) {
      return;
    }
    if (!isOnboardingTourPending()) return;
    if (!tourAllowed) {
      clearOnboardingTourPending();
      return;
    }
    const timer = window.setTimeout(() => {
      if (isOnboardingTourPending()) start();
    }, 800);
    return () => window.clearTimeout(timer);
  }, [pathname, phase, start, tourAllowed]);

  // Manual replay (avatar menu).
  useEffect(() => {
    if (!tourAllowed) return;
    const onReplay = () => start();
    window.addEventListener(START_ONBOARDING_TOUR_EVENT, onReplay);
    return () =>
      window.removeEventListener(START_ONBOARDING_TOUR_EVENT, onReplay);
  }, [start, tourAllowed]);

  // Leaving the layout pages mid-tour closes it without consuming the flag.
  useEffect(() => {
    if (phase !== "idle" && pathname && !TOUR_PAGES.includes(pathname)) {
      setPhase("idle");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Track the highlighted element; re-measure on resize/scroll and on a short
  // interval so late layout shifts (fonts, images) don't leave it misaligned.
  useEffect(() => {
    if (!step) return;
    const openedAt = Date.now();
    let scrolled = false;
    const update = () => {
      setViewport({ w: window.innerWidth, h: window.innerHeight });
      if (!step.targets) {
        setShowingDemo(true);
        setRect(measureElement(demoCardRef.current));
        return;
      }
      // A first-only target (first training card) may sit below the fold:
      // bring it into view once so it can be measured.
      if (step.firstOnly && !scrolled) {
        const first = document.querySelector(
          `[data-tour="${step.targets[0]}"]`
        );
        if (first) {
          scrolled = true;
          if (!isOnScreen(first)) first.scrollIntoView({ block: "center" });
        }
      }
      const real = measureTargets(step.targets, step.firstOnly);
      if (real) {
        setShowingDemo(false);
        setRect(real);
      } else if (
        step.demoFallback &&
        ((step.emptyTarget && targetExists(step.emptyTarget)) ||
          Date.now() - openedAt >= DEMO_FALLBACK_DELAY_MS)
      ) {
        setShowingDemo(true);
        setRect(measureElement(demoCardRef.current));
      } else {
        setShowingDemo(false);
        setRect(null);
      }
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    const interval = window.setInterval(update, 300);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
      window.clearInterval(interval);
    };
  }, [step]);

  // The demo card mounts with no measured rect, so the tooltip would sit
  // centred over it until the next 300ms tick. Measure it before paint.
  useLayoutEffect(() => {
    if (showingDemo && demoCardRef.current) {
      setRect(measureElement(demoCardRef.current));
    }
  }, [showingDemo]);

  // Steps tied to a page (mobile "Your trainings" -> /home) navigate there.
  useEffect(() => {
    if (!step?.route || pathname === step.route) return;
    void router.push(step.route);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    if (phase === "idle") return;
    primaryButtonRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") finish();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, stepIndex, finish]);

  useEffect(() => {
    if (!doneToast) return;
    const timer = window.setTimeout(() => setDoneToast(null), 5000);
    return () => window.clearTimeout(timer);
  }, [doneToast]);

  const firstName = (user?.name || "").trim().split(/\s+/)[0];
  const brand: string = org?.whitelabeling
    ? String(org.organizationName || "")
    : "mple.ai";
  const welcomeTitle = brand
    ? t("tour_welcome_title", "Welcome to {{brand}}", { brand })
    : t("tour_welcome_title_plain", "Welcome");

  const toast = doneToast ? (
    <div
      role="status"
      className="fixed inset-x-4 bottom-[86px] z-[10000] mx-auto flex max-w-sm items-center gap-3 rounded-lg bg-[#1C1B1F] px-4 py-3 text-sm text-white shadow-lg md:bottom-6"
    >
      <MdCheckCircle className="shrink-0 text-green-400" size={20} />
      <span className="flex-1">{doneToast}</span>
      <button
        type="button"
        aria-label={t("close", "Close")}
        onClick={() => setDoneToast(null)}
        className="shrink-0"
      >
        <IoClose size={20} />
      </button>
    </div>
  ) : null;

  if (phase === "idle") return toast;

  // Desktop, between "Start Tour" and the dashboard being ready.
  if (phase === "preparing") {
    return <div className="fixed inset-0 z-[10000] bg-black/45" />;
  }

  if (phase === "welcome") {
    return (
      <div className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/45 md:items-center">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="onboarding-tour-welcome-title"
          className={
            isDesktop
              ? "w-full max-w-sm rounded-2xl bg-cardBg p-6 shadow-xl"
              : "w-full rounded-t-3xl bg-cardBg px-6 pb-8 pt-6 shadow-xl md:max-w-md md:rounded-3xl"
          }
        >
          <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-primary text-onPrimary">
            <MdTagFaces size={24} />
          </div>
          <h2
            id="onboarding-tour-welcome-title"
            className="text-xl font-semibold text-appText"
          >
            {firstName ? `${welcomeTitle}, ${firstName}` : welcomeTitle}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-appText/80">
            {isDesktop
              ? t(
                  "tour_welcome_body_desktop",
                  "Take a quick tour of your dashboard. It takes about 30 seconds."
                )
              : t(
                  "tour_welcome_body",
                  "Take a quick tour of your learning space. It takes about 30 seconds."
                )}
          </p>
          {isDesktop ? (
            <div className="mt-6 flex gap-3">
              <button
                ref={primaryButtonRef}
                type="button"
                onClick={beginSteps}
                className="h-10 flex-1 rounded-lg bg-primary text-sm font-semibold text-onPrimary"
              >
                {t("tour_start", "Start Tour")}
              </button>
              <button
                type="button"
                onClick={finish}
                className="h-10 flex-1 rounded-lg border border-appText/20 text-sm font-medium text-appText"
              >
                {t("tour_skip_for_now", "Skip for now")}
              </button>
            </div>
          ) : (
            <>
              <button
                ref={primaryButtonRef}
                type="button"
                onClick={beginSteps}
                className="mt-6 h-11 w-full rounded-lg bg-primary text-sm font-semibold text-onPrimary"
              >
                {t("tour_start", "Start Tour")}
              </button>
              <button
                type="button"
                onClick={finish}
                className="mt-3 w-full py-2 text-sm font-medium text-appText/80"
              >
                {t("tour_skip_for_now", "Skip for now")}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!step) return toast;

  // ---- Tooltip placement ----
  const vw = viewport.w || window.innerWidth;
  const vh = viewport.h || window.innerHeight;
  const tooltipWidth = Math.min(
    isDesktop ? TOOLTIP_WIDTH_DESKTOP : TOOLTIP_MAX_WIDTH,
    vw - EDGE * 2
  );
  const spotPad = isDesktop ? SPOT_PAD_DESKTOP : SPOT_PAD;
  // Beside the target (desktop sidebar), if there is room on the right.
  const placeRight =
    step.placement === "right" &&
    rect !== null &&
    rect.left + rect.width + spotPad + GAP + tooltipWidth + EDGE <= vw;
  const centerX = rect ? rect.left + rect.width / 2 : vw / 2;
  const tooltipLeft = Math.min(
    Math.max(centerX - tooltipWidth / 2, EDGE),
    vw - tooltipWidth - EDGE
  );
  // Put the tooltip on whichever side of the target has more room.
  const placeAbove = rect ? rect.top + rect.height / 2 > vh / 2 : false;
  const tooltipPosition: React.CSSProperties = placeRight
    ? {
        left: rect.left + rect.width + spotPad + GAP,
        top: Math.min(Math.max(rect.top, EDGE), vh - 240),
      }
    : rect
      ? placeAbove
        ? { bottom: vh - rect.top + GAP, left: tooltipLeft }
        : { top: rect.top + rect.height + GAP, left: tooltipLeft }
      : // Target not on screen (e.g. collapsed sidebar): centre the message.
        { top: "50%", left: tooltipLeft, transform: "translateY(-50%)" };
  const arrowLeft = Math.min(
    Math.max(centerX - tooltipLeft - 8, 20),
    tooltipWidth - 36
  );

  const demoCardWidth = Math.min(320, vw - EDGE * 2);
  // Leave room above the card for the tooltip, but never push it off-screen.
  const demoCardTop = Math.max(
    EDGE,
    Math.min(Math.max(vh * 0.32, 220), vh - 380)
  );

  return (
    <div className="fixed inset-0 z-[10000]" aria-live="polite">
      {/* Dim layer with a cut-out around the target. Also swallows clicks.
          The demo-card step uses a plain dim layer: the card is drawn on top
          of it, so a cut-out would only expose the page behind the card. */}
      {rect && !showingDemo ? (
        <div
          className="pointer-events-none fixed rounded-2xl transition-all duration-200"
          style={{
            top: rect.top - spotPad,
            left: rect.left - spotPad,
            width: rect.width + spotPad * 2,
            height: rect.height + spotPad * 2,
            // Desktop adds a white ring around the highlighted area.
            boxShadow: isDesktop
              ? "0 0 0 2px #ffffff, 0 0 0 9999px rgba(15, 10, 30, 0.45)"
              : "0 0 0 9999px rgba(15, 10, 30, 0.45)",
          }}
        />
      ) : (
        <div className="fixed inset-0 bg-black/45" />
      )}

      {showingDemo && (
        <div
          ref={demoCardRef}
          aria-hidden="true"
          className="pointer-events-none fixed"
          style={{
            top: demoCardTop,
            left: (vw - demoCardWidth) / 2,
            width: demoCardWidth,
          }}
        >
          <TrainingBookCard
            val={DEMO_TRAINING}
            index={0}
            userRole="user"
            isGrid
            hideCta
          />
        </div>
      )}

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-tour-step-title"
        className="fixed rounded-2xl bg-cardBg p-4 shadow-xl"
        style={{
          ...tooltipPosition,
          width: tooltipWidth,
          // Never draw the tooltip over the demo card before it is placed.
          visibility: showingDemo && !rect ? "hidden" : undefined,
        }}
      >
        {rect && (
          <span
            aria-hidden="true"
            className="absolute h-4 w-4 rotate-45 bg-cardBg"
            style={
              placeRight
                ? { left: -7, top: 20 }
                : {
                    left: arrowLeft,
                    ...(placeAbove ? { bottom: -7 } : { top: -7 }),
                  }
            }
          />
        )}

        <div className="relative flex items-center justify-between">
          <div className="flex gap-1.5" aria-hidden="true">
            {steps.map((s, i) => (
              <span
                key={s.id}
                className={`h-1.5 w-1.5 rounded-full ${
                  i === stepIndex ? "bg-primary" : "bg-primary/20"
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={finish}
            className="text-xs text-appText/70"
          >
            {isDesktop
              ? t("tour_skip_tour", "Skip tour")
              : t("tour_skip", "Skip")}
          </button>
        </div>

        <h3
          id="onboarding-tour-step-title"
          className="relative mt-3 text-base font-semibold text-appText"
        >
          {t(step.titleKey, step.title)}
        </h3>
        <p className="relative mt-1 text-sm leading-relaxed text-appText/80">
          {t(step.bodyKey, step.body)}
        </p>

        <div className="relative mt-4 flex items-center justify-between">
          {stepIndex > 0 ? (
            <button
              type="button"
              onClick={() => setStepIndex((i) => i - 1)}
              className="px-2 py-2 text-sm font-semibold text-primary"
            >
              {t("tour_back", "Back")}
            </button>
          ) : isDesktop ? (
            // Desktop shows Back greyed out on the first step.
            <button
              type="button"
              disabled
              className="px-2 py-2 text-sm font-semibold text-appText/30"
            >
              {t("tour_back", "Back")}
            </button>
          ) : (
            <span />
          )}
          <button
            ref={primaryButtonRef}
            type="button"
            onClick={() => (isLastStep ? finish() : setStepIndex((i) => i + 1))}
            className="h-9 rounded-lg bg-primary px-5 text-sm font-semibold text-onPrimary"
          >
            {isLastStep
              ? t("tour_done_button", "Done")
              : t("tour_next", "Next")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
